class ChipLinks extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section')
    this.list = this.querySelector('[data-chip-links-list]')
    this.links = Array.from(this.querySelectorAll('[data-chip-link]'))
    this.select = this.querySelector('[data-chip-links-select]')
    this.isSlider = this.hasAttribute('data-slider')
    this.isSticky = this.hasAttribute('data-sticky')
    this.showOnScroll = this.hasAttribute('data-show-on-scroll')
    this.showOffset = parseInt(this.getAttribute('data-show-offset') || '200', 10)

    this._onSelectChange = this._onSelectChange.bind(this)
    this._onLinkClick = this._onLinkClick.bind(this)
    this._onScroll = this._onScroll.bind(this)
    this._onResize = this._onResize.bind(this)

    this.select?.addEventListener('change', this._onSelectChange)
    this.links.forEach((link) => link.addEventListener('click', this._onLinkClick))

    this._syncActiveFromLocation()
    this._observeHashTargets()
    this._applyScrollMargins()

    if (this.showOnScroll || this.isSticky) {
      this._onScroll()
      window.addEventListener('scroll', this._onScroll, { passive: true })
      window.addEventListener('resize', this._onResize, { passive: true })
    }

    if (this.isSlider) {
      requestAnimationFrame(() => this._centerActiveChip(false))
    }
  }

  disconnectedCallback() {
    this.select?.removeEventListener('change', this._onSelectChange)
    this.links.forEach((link) => link.removeEventListener('click', this._onLinkClick))
    window.removeEventListener('scroll', this._onScroll)
    window.removeEventListener('resize', this._onResize)
    this._observer?.disconnect()
  }

  _onSelectChange(event) {
    const option = event.target.selectedOptions[0]
    if (!option?.value) return

    if (option.dataset.newTab === 'true') {
      window.open(option.value, '_blank', 'noopener')
      return
    }

    this._navigate(option.value)
  }

  _onLinkClick(event) {
    const link = event.currentTarget
    const url = this._parseLink(link)
    const samePage =
      url &&
      url.pathname === window.location.pathname &&
      url.search === window.location.search &&
      url.hash

    if (samePage && document.querySelector(url.hash)) {
      event.preventDefault()
      this._navigate(link.getAttribute('href'))
    }

    this._setActive(link)

    if (this.isSlider) {
      this._centerChip(link, true)
    }

    if (this.select) {
      this.select.value = link.getAttribute('href')
    }
  }

  _onScroll() {
    if (!this.section) return

    if (this.showOnScroll) {
      const visible = window.scrollY >= this.showOffset
      this.section.classList.toggle('is-chip-links-visible', visible)
      // Fixed bar: "stuck" whenever it is visible
      this.section.classList.toggle('is-stuck', visible)
    } else if (this.isSticky) {
      const stickyTop = this._cssPxVar('--sticky-area-height')
      const isStuck = this.section.getBoundingClientRect().top <= stickyTop + 1
      this.section.classList.toggle('is-stuck', isStuck)
    }

    this._applyScrollMargins()
  }

  _onResize() {
    this._applyScrollMargins()
  }

  _cssPxVar(name) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    const value = parseFloat(raw)
    return Number.isFinite(value) ? value : 0
  }

  _scrollOffset() {
    const stickyArea = this._cssPxVar('--sticky-area-height')
    const isStuck = this.section?.classList.contains('is-stuck')
    const chipHeight = this.isSticky && isStuck ? this.offsetHeight : 0
    return stickyArea + chipHeight + 12
  }

  _applyScrollMargins() {
    if (!this.isSticky && !this.showOnScroll) return

    const offset = this._scrollOffset()
    document.documentElement.style.setProperty('--chip-links-scroll-offset', `${offset}px`)

    this.links.forEach((link) => {
      const url = this._parseLink(link)
      if (!url?.hash) return
      const el = document.querySelector(url.hash)
      if (el) el.style.scrollMarginTop = `${offset}px`
    })
  }

  _navigate(href) {
    try {
      const url = new URL(href, window.location.href)
      const samePage =
        url.pathname === window.location.pathname && url.search === window.location.search

      if (samePage && url.hash) {
        const target = document.querySelector(url.hash)
        if (target) {
          this._applyScrollMargins()
          const top =
            target.getBoundingClientRect().top + window.scrollY - this._scrollOffset()
          window.scrollTo({
            top: Math.max(0, top),
            behavior: window.matchMedia('(prefers-reduced-motion: no-preference)').matches
              ? 'smooth'
              : 'auto',
          })
          history.replaceState(null, '', url.hash)
          return
        }
      }
    } catch (error) {
      // Fall through
    }

    window.location.href = href
  }

  _parseLink(link) {
    try {
      return new URL(link.href, window.location.href)
    } catch (error) {
      return null
    }
  }

  _syncActiveFromLocation() {
    const { pathname, search, hash } = window.location
    let matched = null

    if (hash) {
      matched = this.links.find((link) => {
        const url = this._parseLink(link)
        return url && url.pathname === pathname && url.search === search && url.hash === hash
      })
    }

    if (!matched) {
      matched = this.links.find((link) => {
        const url = this._parseLink(link)
        return url && url.pathname === pathname && url.search === search && !url.hash
      })
    }

    if (matched) {
      this._setActive(matched)
      if (this.select) this.select.value = matched.getAttribute('href')
    }
  }

  _observeHashTargets() {
    const targets = []

    this.links.forEach((link) => {
      const url = this._parseLink(link)
      if (!url || url.pathname !== window.location.pathname || !url.hash) return
      const el = document.querySelector(url.hash)
      if (el) targets.push({ el, link })
    })

    if (!targets.length || !('IntersectionObserver' in window)) return

    const topOffset = this._scrollOffset()

    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const match = targets.find((item) => item.el === entry.target)
          if (!match) return
          this._setActive(match.link)
          if (this.select) this.select.value = match.link.getAttribute('href')
          if (this.isSlider) this._centerChip(match.link, true)
        })
      },
      {
        rootMargin: `-${Math.max(topOffset, 80)}px 0px -65% 0px`,
        threshold: 0,
      }
    )

    targets.forEach(({ el }) => this._observer.observe(el))
  }

  _setActive(activeLink) {
    this.links.forEach((link) => {
      const isActive = link === activeLink
      link.classList.toggle('is-active', isActive)
      if (isActive) {
        link.setAttribute('aria-current', 'true')
      } else {
        link.removeAttribute('aria-current')
      }
    })
  }

  _centerActiveChip(smooth = true) {
    const active = this.querySelector('.chip-links__link.is-active')
    if (active) this._centerChip(active, smooth)
  }

  _centerChip(link, smooth = true) {
    if (!this.list || !link) return

    const listRect = this.list.getBoundingClientRect()
    const linkRect = link.getBoundingClientRect()
    const offset =
      linkRect.left - listRect.left - listRect.width / 2 + linkRect.width / 2 + this.list.scrollLeft

    this.list.scrollTo({
      left: offset,
      behavior:
        smooth && window.matchMedia('(prefers-reduced-motion: no-preference)').matches
          ? 'smooth'
          : 'auto',
    })
  }
}

if (!customElements.get('chip-links')) {
  customElements.define('chip-links', ChipLinks)
}
